// src/pages/admin/property/room/TableRoomsProperty.tsx
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getRoomTypeIndex,
  getAdminRoomTypeDetail,
  patchAdminRoomType,
} from '@/API/apiService';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Room {
  room_type_id: number;
  propertyId: number;
  site_controller_id: number;
  site_controller_room_type_id: number;
  name: string;
  name_en: string;
  is_published: boolean;
  sort_number: number;
}

export const TableRoomsProperty: React.FC = () => {
  const { propertyId } = useParams<{ propertyId: string }>();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [orderChanged, setOrderChanged] = useState<boolean>(false);
  console.log(rooms, '中身');

  useEffect(() => {
    const getData = async () => {
      if (!propertyId) {
        setError('propertyId が指定されていません。');
        setLoading(false);
        return;
      }
      try {
        const response = await getRoomTypeIndex(propertyId);
        const roomsData = response.data.data.room_types;
        console.log(roomsData, '部屋情報');
        const mapped: Room[] = Array.isArray(roomsData)
          ? roomsData.map((item: any, index: number) => ({
              room_type_id: item.room_type_id,
              propertyId: item.property
                ? item.property.id
                : Number(item.property_id),
              site_controller_id: item.site_controller_id,
              site_controller_room_type_id: item.site_controller_room_type_id,
              name: item.name,
              name_en: item.name_en,
              is_published:
                item.is_published === true ||
                item.is_published === 1 ||
                item.is_published === '1',
              sort_number:
                item.sort_number !== undefined ? item.sort_number : index + 1,
            }))
          : [];
        mapped.sort((a, b) => a.sort_number - b.sort_number);
        setRooms(mapped);
      } catch (err) {
        console.error('Error fetching rooms:', err);
        setError('部屋情報の取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    };

    getData();
  }, [propertyId]);

  // dnd-kit 用センサー設定
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // 更新処理のためのヘルパー関数
  const updateRoom = async (room: Room) => {
    if (!propertyId) return;
    try {
      // 詳細情報を取得
      const detailResponse = await getAdminRoomTypeDetail(
        propertyId,
        room.room_type_id.toString()
      );
      const data = detailResponse.data.data;

      // 一覧で変更された sort_number, is_published を上書き
      const updatedData = {
        ...data,
        site_controller_id: data.site_controller_id
          ? data.site_controller_id.toString()
          : '1',
        site_controller_room_type_id: data.site_controller_room_type_id || '',
        chillnn_id: data.chillnn_id || '',
        room_type_name: data.room_type_name || '',
        room_type_name_en: data.room_type_name_en || '',
        capacity: data.capacity ? data.capacity.toString() : '',
        room_area: data.room_area ? data.room_area.toString() : '',
        floor_plan: data.floor_plan || '',
        description: data.description || '',
        description_en: data.description_en || '',
        bath_amenity: data.bath_amenity || '',
        bath_amenity_en: data.bath_amenity_en || '',
        price: data.price ? data.price.toString() : '',
        floor_plan_image: data.floor_plan_image || {
          image_id: null,
          url: null,
        },
        sort_number: room.sort_number.toString(), // 更新済み並び順
        area_id: data.area_id ? data.area_id.toString() : '',
        information: data.information || '',
        information_en: data.information_en || '',
        property_id: data.property_id ? data.property_id.toString() : '',
        property_name: data.property_name || '',
        property_name_en: data.property_name_en || '',
        region_id: data.region_id ? data.region_id.toString() : '',
        room_type_id: data.room_type_id ? data.room_type_id.toString() : '',
        facility: data.facility || '',
        facility_en: data.facility_en || '',
        is_published: room.is_published, // 更新済み公開状態
      };

      const formDataToSend = new FormData();
      formDataToSend.append('property_id', updatedData.property_id);
      formDataToSend.append('room_type_id', updatedData.room_type_id);
      formDataToSend.append(
        'site_controller_id',
        updatedData.site_controller_id
      );
      formDataToSend.append(
        'site_controller_room_type_id',
        updatedData.site_controller_room_type_id
      );
      formDataToSend.append('chillnn_id', updatedData.chillnn_id);
      // 送信時は name / name_en に変換
      formDataToSend.append('name', updatedData.room_type_name);
      formDataToSend.append('name_en', updatedData.room_type_name_en);
      formDataToSend.append('capacity', updatedData.capacity);
      formDataToSend.append('room_area', updatedData.room_area);
      formDataToSend.append('floor_plan', updatedData.floor_plan);
      formDataToSend.append('description', updatedData.description);
      formDataToSend.append('description_en', updatedData.description_en);
      formDataToSend.append('bath_amenity', updatedData.bath_amenity);
      formDataToSend.append('bath_amenity_en', updatedData.bath_amenity_en);
      formDataToSend.append('price', updatedData.price);

      // floor_plan_image が有効な場合のみ送信
      if (
        updatedData.floor_plan_image &&
        updatedData.floor_plan_image.image_id &&
        updatedData.floor_plan_image.url
      ) {
        formDataToSend.append(
          'floor_plan_image',
          JSON.stringify(updatedData.floor_plan_image)
        );
      }

      formDataToSend.append('sort_number', updatedData.sort_number);
      formDataToSend.append('area_id', updatedData.area_id);
      formDataToSend.append('information', updatedData.information);
      formDataToSend.append('information_en', updatedData.information_en);
      formDataToSend.append('property_name', updatedData.property_name);
      formDataToSend.append('property_name_en', updatedData.property_name_en);
      formDataToSend.append('region_id', updatedData.region_id);
      formDataToSend.append('facility', updatedData.facility);
      formDataToSend.append('facility_en', updatedData.facility_en);
      formDataToSend.append(
        'is_published',
        updatedData.is_published ? 'true' : 'false'
      );

      await patchAdminRoomType(propertyId, room.room_type_id, formDataToSend);
    } catch (error) {
      console.error(`Failed to update room ${room.room_type_id}:`, error);
    }
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rooms.findIndex(
      (room) => room.room_type_id.toString() === active.id
    );
    const newIndex = rooms.findIndex(
      (room) => room.room_type_id.toString() === over.id
    );
    const newRooms = arrayMove(rooms, oldIndex, newIndex).map(
      (room, index) => ({
        ...room,
        sort_number: index + 1,
      })
    );
    setRooms(newRooms);
    setOrderChanged(true);
  };

  const handleTogglePublished = async (roomTypeId: number) => {
    const updatedRooms = rooms.map((room) =>
      room.room_type_id === roomTypeId
        ? { ...room, is_published: !room.is_published }
        : room
    );
    setRooms(updatedRooms);
    setOrderChanged(true);
    const roomToUpdate = updatedRooms.find(
      (room) => room.room_type_id === roomTypeId
    );
    if (roomToUpdate) {
      await updateRoom(roomToUpdate);
    }
  };

  const confirmSortUpdate = async () => {
    try {
      await Promise.all(rooms.map(async (room) => await updateRoom(room)));
      setOrderChanged(false);
    } catch (error) {
      console.error('Failed to update sort order:', error);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="overflow-x-auto">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={rooms.map((room) => room.room_type_id.toString())}
          strategy={verticalListSortingStrategy}
        >
          <table className="min-w-full bg-white rounded">
            <thead className="bg-white border-b-2 border-[#DDDDDD]">
              <tr>
                <th className="p-3 text-left text-black text-sm">並び替え</th>
                <th className="p-3 text-left text-black text-sm">部屋名</th>
                <th className="p-3 text-left text-black text-sm">ステータス</th>
                <th className="p-3 text-left text-black text-sm">操作</th>
              </tr>
            </thead>
            <tbody>
              {rooms.map((room) => (
                <SortableRow
                  key={room.room_type_id.toString()}
                  room={room}
                  onTogglePublished={handleTogglePublished}
                  propertyId={propertyId} // useParams から取得した値を渡す
                />
              ))}
            </tbody>
          </table>
        </SortableContext>
      </DndContext>
      {orderChanged && (
        <div className="my-4 text-center">
          <button
            onClick={confirmSortUpdate}
            className="px-4 py-2 bg-black text-white rounded"
          >
            確定
          </button>
        </div>
      )}
    </div>
  );
};

interface SortableRowProps {
  room: Room;
  onTogglePublished: (roomTypeId: number) => void;
  propertyId?: string; // ルートパラメータからの propertyId を受け取る
}

const SortableRow: React.FC<SortableRowProps> = ({
  room,
  onTogglePublished,
  propertyId,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: room.room_type_id.toString(),
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`border-b border-[#DDDDDD] ${isDragging ? 'bg-gray-100' : ''}`}
      {...attributes}
      {...listeners}
    >
      <td className="px-4 py-4 text-black text-sm cursor-grab select-none">
        ≡
      </td>
      <td className="px-4 py-4 text-black text-sm">{room.name}</td>
      <td className="px-4 py-4 text-black text-sm">
        <button onClick={() => onTogglePublished(room.room_type_id)}>
          {room.is_published ? '公開中' : '非公開'}
        </button>
      </td>
      <td className="px-4 py-4 text-black text-xs">
        <Link
          to={`/admin/property/${Number(propertyId)}/room/${room.room_type_id}/edit`}
          className="bg-white px-2 py-1 border rounded text-xs cursor-pointer text-center"
        >
          編集
        </Link>
      </td>
    </tr>
  );
};

export default TableRoomsProperty;
